import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getJournal, saveJournalEntry, deleteJournalEntry, sendJournalToManager, GetJournalOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, BookOpen, Send, CheckCircle, MessageSquare, StickyNote, ExternalLink, Download, Printer } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type Entry = GetJournalOutputType['entries'][0];
type Module = GetJournalOutputType['modules'][0];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

const LOGO_URL = 'https://staffhub.wcdns.co.uk/rattlebone-logo.png';

function buildJournalPdf(entries: Entry[]): string {
  const year = new Date().getFullYear();
  const content = entries.map(e => `
    <div style="margin-bottom:32px;padding-bottom:24px;border-bottom:1px solid #eee">
      <p style="font-size:11px;color:#999;margin-bottom:4px">${e.entryDate ? fmtDate(e.entryDate) : ''}${e.sourcePage ? ` · ${e.sourcePage}` : ''}${e.isQuestion ? ' · Question' : ''}</p>
      <h2 style="font-size:16px;font-weight:700;margin-bottom:12px;color:#1a1a1a">${e.entryTitle}</h2>
      <p style="font-size:14px;color:#333;white-space:pre-wrap;line-height:1.6">${e.entryContent}</p>
      ${e.managerResponse ? `<div style="margin-top:12px;padding:10px 14px;background:#faf6f4;border-left:3px solid #A36B58;border-radius:0 4px 4px 0"><p style="font-size:11px;font-weight:600;color:#A36B58;margin-bottom:4px">Manager Response</p><p style="font-size:13px;color:#333">${e.managerResponse}</p></div>` : ''}
    </div>
  `).join('');
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>My Learning Journal</title><style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#1a1a1a}.page{max-width:760px;margin:0 auto;padding:48px 40px}.header{display:flex;align-items:center;justify-content:space-between;padding-bottom:20px;border-bottom:3px solid #A36B58;margin-bottom:36px}.logo{height:52px}@media print{@page{margin:18mm 20mm}}</style></head>
<body><div class="page"><div class="header"><img class="logo" src="${LOGO_URL}" alt="Rattlebone"/><span style="font-size:13px;color:#888;text-transform:uppercase;font-weight:500">Learning Journal</span></div>${content}<div style="margin-top:40px;padding-top:16px;border-top:1px solid #ddd;font-size:11px;color:#aaa">© Rattlebone Sherston Ltd ${year}</div></div></body></html>`;
}

export default function Journal() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Entry | null>(null);
  const [editing, setEditing] = useState(false);
  const [filter, setFilter] = useState<'all' | 'questions' | 'notes' | 'answered'>('all');
  const [form, setForm] = useState({ entryTitle: '', entryContent: '', moduleId: '' });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => {
    if (!user) return;
    getJournal({}).then(d => { setEntries(d.entries); setModules(d.modules); }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [user]);

  const filtered = entries.filter(e => {
    if (filter === 'questions') return e.isQuestion;
    if (filter === 'notes') return e.sourcePage && !e.isQuestion;
    if (filter === 'answered') return !!e.managerResponse;
    return true;
  });

  const startNew = () => {
    setSelected(null);
    setForm({ entryTitle: '', entryContent: '', moduleId: modules[0]?.id || '' });
    setEditing(true);
  };

  const startEdit = (e: Entry) => {
    setSelected(e);
    setForm({ entryTitle: e.entryTitle, entryContent: e.entryContent, moduleId: e.moduleId });
    setEditing(true);
  };

  const save = async () => {
    if (!form.entryTitle.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await saveJournalEntry({ id: selected?.id, entryTitle: form.entryTitle, entryContent: form.entryContent, moduleId: form.moduleId || modules[0]?.id || '' });
      toast.success(selected ? 'Entry updated' : 'Entry saved');
      setEditing(false);
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    try {
      await deleteJournalEntry({ id });
      toast.success('Entry deleted');
      if (selected?.id === id) { setSelected(null); setEditing(false); }
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleSendToManager = async (entry: Entry) => {
    setSending(true);
    try {
      await sendJournalToManager({ entryId: entry.id });
      toast.success('Sent to your manager for review');
      load();
    } catch (e: any) { toast.error(e.message || 'Failed to send'); }
    finally { setSending(false); }
  };

  const handlePrint = () => {
    const toPrint = selected ? [selected] : filtered;
    if (!toPrint.length) { toast.error('No entries to print'); return; }
    const win = window.open('', '_blank', 'width=900,height=750');
    if (!win) { toast.error('Allow pop-ups to print'); return; }
    win.document.write(buildJournalPdf(toPrint));
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 600);
  };

  if (loading) return (
    <div className="flex h-[calc(100vh-56px)]">
      <div className="w-72 border-r border-border p-4 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      <div className="flex-1 p-8"><Skeleton className="h-64" /></div>
    </div>
  );

  return (
    <div className="flex" style={{ height: 'calc(100vh - 56px)' }}>
      {/* Sidebar */}
      <aside className="w-72 border-r border-border bg-card flex flex-col shrink-0">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-semibold text-foreground">Learning Journal</h2>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handlePrint} title="Print / Download">
              <Printer className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" onClick={startNew} className="h-7 px-2">
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <div className="p-3 border-b border-border">
          <div className="flex gap-1 p-0.5 bg-muted rounded-lg text-xs">
            {(['all', 'notes', 'questions', 'answered'] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`flex-1 py-1 rounded-md font-medium transition-colors capitalize ${filter === f ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}>
                {f === 'answered' ? '✓ Answered' : f}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-border">
          {filtered.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">
              <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No entries yet
            </div>
          ) : (
            filtered.map(e => (
              <button key={e.id} onClick={() => { setSelected(e); setEditing(false); }}
                className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors ${selected?.id === e.id && !editing ? 'bg-muted' : ''}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  {e.isQuestion && <MessageSquare className="w-3 h-3 text-primary shrink-0" />}
                  {e.sourcePage && !e.isQuestion && <StickyNote className="w-3 h-3 text-muted-foreground shrink-0" />}
                  <p className="text-sm font-medium text-foreground truncate">{e.entryTitle}</p>
                </div>
                <p className="text-xs text-muted-foreground truncate">{e.sourcePage || e.moduleTitle}</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-muted-foreground">{e.entryDate}</p>
                  {e.managerResponse && <span className="text-xs text-primary">✓ Answered</span>}
                  {e.sentToManager && !e.managerResponse && <span className="text-xs text-amber-600">Sent</span>}
                </div>
              </button>
            ))
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto bg-background p-8">
        {editing ? (
          <div className="max-w-xl space-y-4">
            <h3 className="font-bold text-foreground text-lg">{selected ? 'Edit Entry' : 'New Journal Entry'}</h3>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Title</label>
              <Input value={form.entryTitle} onChange={e => setForm(f => ({ ...f, entryTitle: e.target.value }))} placeholder="What did you learn today?" />
            </div>
            {modules.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Module (optional)</label>
                <Select value={form.moduleId || 'none'} onValueChange={v => setForm(f => ({ ...f, moduleId: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select module" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">General note</SelectItem>
                    {modules.map(m => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Your reflections</label>
              <Textarea value={form.entryContent} onChange={e => setForm(f => ({ ...f, entryContent: e.target.value }))} placeholder="What did you learn, find challenging, or want to remember?" className="min-h-48" />
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save entry'}</Button>
              <Button variant="outline" onClick={() => { setEditing(false); setSelected(null); }}>Cancel</Button>
            </div>
          </div>
        ) : selected ? (
          <div className="max-w-xl">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {selected.isQuestion && <Badge variant="outline" className="text-xs">Question</Badge>}
                  {selected.sourcePage && <Badge variant="outline" className="text-xs text-muted-foreground">{selected.sourcePage}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{selected.entryDate ? fmtDate(selected.entryDate) : ''}</p>
                <h3 className="text-xl font-bold text-foreground mt-1">{selected.entryTitle}</h3>
              </div>
              <div className="flex gap-1.5">
                {selected.sourcePath && (
                  <a href={selected.sourcePath}>
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1"><ExternalLink className="w-3 h-3" /> View page</Button>
                  </a>
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => startEdit(selected)}>Edit</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handlePrint} title="Print"><Printer className="w-3 h-3" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete this entry?</AlertDialogTitle></AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(selected.id)}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>

            <p className="text-foreground leading-relaxed whitespace-pre-wrap mt-4">{selected.entryContent || <span className="text-muted-foreground italic">No content.</span>}</p>

            {/* Manager response */}
            {selected.managerResponse && (
              <div className="mt-5 border-l-4 border-primary/50 bg-primary/5 rounded-r-lg px-4 py-3">
                <p className="text-xs font-semibold text-primary mb-1">💬 Manager Response</p>
                <p className="text-sm text-foreground leading-relaxed">{selected.managerResponse}</p>
                {selected.managerRespondedAt && <p className="text-xs text-muted-foreground mt-1">{fmtDate(selected.managerRespondedAt)}</p>}
              </div>
            )}

            {/* Send to manager */}
            {selected.isQuestion && !selected.sentToManager && !selected.managerResponse && (
              <div className="mt-4 p-3 bg-muted/30 border border-border rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">This question hasn't been sent to your manager yet.</p>
                <Button size="sm" className="gap-1.5" onClick={() => handleSendToManager(selected)} disabled={sending}>
                  {sending ? 'Sending…' : <><Send className="w-3.5 h-3.5" /> Send to Manager</>}
                </Button>
              </div>
            )}
            {selected.sentToManager && !selected.managerResponse && (
              <div className="mt-4 flex items-center gap-2 text-xs text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                <Send className="w-3.5 h-3.5" /> Sent to your manager — awaiting response
              </div>
            )}
            {selected.managerResponse && (
              <div className="mt-2 flex items-center gap-2 text-xs text-primary">
                <CheckCircle className="w-3.5 h-3.5" /> Answered by your manager
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <BookOpen className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground mb-2">Select an entry or start a new one</p>
            <p className="text-xs text-muted-foreground mb-4">Notes from the Handbook and Policies appear here automatically</p>
            <Button onClick={startNew}><Plus className="w-4 h-4 mr-1" /> New entry</Button>
          </div>
        )}
      </main>
    </div>
  );
}
